/**
 * GenericWidget - Fallback widget for unknown types
 *
 * Displays raw JSON data in a formatted way
 */

import { useState } from 'react';

/**
 * JSON Tree renderer
 */
function JsonTree({ data, depth = 0 }) {
    if (data === null) return <span className="text-gray-400">null</span>;
    if (data === undefined) return <span className="text-gray-400">undefined</span>;

    if (typeof data === 'string') {
        return <span className="text-green-600 dark:text-green-400">"{data}"</span>;
    }

    if (typeof data === 'number') {
        return <span className="text-blue-600 dark:text-blue-400">{data}</span>;
    }

    if (typeof data === 'boolean') {
        return <span className="text-purple-600 dark:text-purple-400">{data.toString()}</span>;
    }

    if (Array.isArray(data)) {
        if (data.length === 0) return <span>[]</span>;

        return (
            <span>
                [
                <div className="pl-4">
                    {data.slice(0, 10).map((item, index) => (
                        <div key={index}>
                            <JsonTree data={item} depth={depth + 1} />
                            {index < data.length - 1 && ','}
                        </div>
                    ))}
                    {data.length > 10 && (
                        <div className="text-gray-400">... {data.length - 10} more items</div>
                    )}
                </div>
                ]
            </span>
        );
    }

    if (typeof data === 'object') {
        const keys = Object.keys(data);
        if (keys.length === 0) return <span>{'{}'}</span>;

        return (
            <span>
                {'{'}
                <div className="pl-4">
                    {keys.slice(0, 20).map((key, index) => (
                        <div key={key}>
                            <span className="text-red-600 dark:text-red-400">"{key}"</span>
                            : <JsonTree data={data[key]} depth={depth + 1} />
                            {index < keys.length - 1 && ','}
                        </div>
                    ))}
                    {keys.length > 20 && (
                        <div className="text-gray-400">... {keys.length - 20} more keys</div>
                    )}
                </div>
                {'}'}
            </span>
        );
    }

    return <span>{String(data)}</span>;
}

/**
 * Main GenericWidget component
 */
export default function GenericWidget({ data, config }) {
    const [expanded, setExpanded] = useState(false);

    const widgetType = config?.type || 'unknown';

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                        Widget: {widgetType}
                    </span>
                </div>
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                    {expanded ? 'Thu gọn' : 'Mở rộng'}
                </button>
            </div>

            {/* Content */}
            <div className={`p-4 overflow-auto ${expanded ? 'max-h-96' : 'max-h-40'}`}>
                <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    <JsonTree data={data} />
                </pre>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    Widget type "{widgetType}" chưa được hỗ trợ. Hiển thị raw data.
                </span>
            </div>
        </div>
    );
}
